import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Send,
  Plus,
  ArrowLeft,
  FileText,
  Mail,
  BarChart3,
  Eye,
  Pencil,
  MoreHorizontal,
  Users,
  CheckCircle,
  Palette,
  ShieldCheck,
  Trash2,
  Monitor,
  Smartphone,
  Search,
  Tag,
  UserCheck,
  LayoutTemplate,
  Save,
  X,
  Check,
} from 'lucide-react';
import { renderToStaticMarkup, type TReaderDocument } from '@usewaypoint/email-builder';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import EmailBlockEditor, {
  createEmptyEmailDocument,
  type EmailDocument,
} from '@/components/email-editor/EmailBlockEditor';
import DeliverabilityPanel from '@/components/email-editor/DeliverabilityPanel';

// ── Types ──

interface Campaign {
  id: number;
  project_id: string;
  subject: string;
  html_content?: string;
  text_content?: string;
  json_content?: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent';
  sent_at?: string;
  recipients?: number;
  delivered?: number;
  opened?: number;
  clicked?: number;
  bounced?: number;
  unsubscribed?: number;
  created_at: string;
  updated_at: string;
}

interface D1Subscriber {
  email: string;
  first_name?: string;
  last_name?: string;
  tags?: string;
}

interface Template {
  id: number;
  project_id: string;
  name: string;
  description?: string;
  category: string;
  json_content?: string;
  html_preview?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

type View = 'list' | 'compose';
type AudienceType = 'all' | 'tags' | 'selected';
type PreviewDevice = 'desktop' | 'mobile';

export default function CampaignsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  // ── View state ──
  const [view, setView] = useState<View>('list');
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [subject, setSubject] = useState('');
  const [emailDoc, setEmailDoc] = useState<EmailDocument>(createEmptyEmailDocument());
  const [composerTab, setComposerTab] = useState('design');
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');

  // ── Send dialog state ──
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendingCampaignId, setSendingCampaignId] = useState<number | null>(null);
  const [audienceType, setAudienceType] = useState<AudienceType>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [subscriberSearch, setSubscriberSearch] = useState('');

  // ── Template dialog state ──
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');

  // ── Delete dialog state ──
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingCampaignId, setDeletingCampaignId] = useState<number | null>(null);

  // ── Render HTML ──
  const renderedHtml = useMemo(() => {
    try {
      const childrenIds = (emailDoc.root?.data as any)?.childrenIds;
      if (!childrenIds || childrenIds.length === 0) return '';
      return renderToStaticMarkup(emailDoc as TReaderDocument, { rootBlockId: 'root' });
    } catch {
      return '';
    }
  }, [emailDoc]);

  // ── Queries ──

  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['newsletter-campaigns', projectId],
    queryFn: async () => {
      const res = await api.get(`/newsletter/campaigns/${projectId}`);
      return res.data;
    },
    enabled: Boolean(projectId),
  });
  const campaigns: Campaign[] = campaignsData?.data || [];

  const { data: subscribersData } = useQuery({
    queryKey: ['campaign-subscribers', projectId, subscriberSearch],
    queryFn: async () => {
      const res = await api.get(
        `/newsletter/campaigns/${projectId}/subscribers${subscriberSearch ? `?search=${encodeURIComponent(subscriberSearch)}` : ''}`
      );
      return res.data;
    },
    enabled: Boolean(projectId) && (showSendConfirm || audienceType !== 'all'),
  });
  const availableSubscribers: D1Subscriber[] = subscribersData?.data?.subscribers || [];
  const availableTags: string[] = subscribersData?.data?.tags || [];

  const { data: templatesData } = useQuery({
    queryKey: ['newsletter-templates', projectId],
    queryFn: async () => {
      const res = await api.get(`/newsletter/templates/${projectId}`);
      return res.data;
    },
    enabled: Boolean(projectId),
  });
  const templates: Template[] = templatesData?.data || [];

  // ── Mutations ──

  const createMutation = useMutation({
    mutationFn: async (data: {
      subject: string;
      htmlContent: string;
      jsonContent: EmailDocument;
    }) => {
      const res = await api.post(`/newsletter/campaigns/${projectId}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns', projectId] });
      toast.success('Campaign saved as draft');
      resetComposer();
    },
    onError: () => toast.error('Failed to save campaign'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { subject: string; htmlContent: string; jsonContent: EmailDocument };
    }) => {
      await api.put(`/newsletter/campaigns/${projectId}/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns', projectId] });
      toast.success('Campaign updated');
      resetComposer();
    },
    onError: () => toast.error('Failed to update campaign'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (campaignId: number) => {
      await api.delete(`/newsletter/campaigns/${projectId}/${campaignId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns', projectId] });
      toast.success('Campaign deleted');
      setShowDeleteConfirm(false);
      setDeletingCampaignId(null);
    },
    onError: () => toast.error('Failed to delete campaign'),
  });

  const sendMutation = useMutation({
    mutationFn: async (campaignId: number) => {
      const payload: any = { audience: audienceType };
      if (audienceType === 'tags') payload.tags = selectedTags;
      if (audienceType === 'selected') payload.subscriberEmails = selectedEmails;
      const res = await api.post(`/newsletter/campaigns/${projectId}/${campaignId}/send`, payload);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-campaigns', projectId] });
      const d = data.data;
      toast.success(
        `Sent to ${d.recipients} subscribers (${d.delivered} delivered, ${d.failed} failed)`
      );
      closeSendDialog();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message ?? 'Failed to send campaign');
      closeSendDialog();
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      jsonContent: EmailDocument;
      htmlPreview: string;
    }) => {
      const res = await api.post(`/newsletter/templates/${projectId}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates', projectId] });
      toast.success('Template saved');
      setShowSaveTemplate(false);
      setTemplateName('');
      setTemplateDescription('');
    },
    onError: () => toast.error('Failed to save template'),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      await api.delete(`/newsletter/templates/${projectId}/${templateId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates', projectId] });
      toast.success('Template deleted');
    },
    onError: () => toast.error('Failed to delete template'),
  });

  // ── Helpers ──

  function resetComposer() {
    setView('list');
    setEditingCampaign(null);
    setSubject('');
    setEmailDoc(createEmptyEmailDocument());
    setComposerTab('design');
    setAudienceType('all');
    setSelectedTags([]);
    setSelectedEmails([]);
  }

  function openComposer(campaign?: Campaign) {
    if (campaign) {
      setEditingCampaign(campaign);
      setSubject(campaign.subject);
      if (campaign.json_content) {
        try {
          setEmailDoc(JSON.parse(campaign.json_content));
        } catch {
          setEmailDoc(createEmptyEmailDocument());
        }
      } else {
        setEmailDoc(createEmptyEmailDocument());
      }
    } else {
      setEditingCampaign(null);
      setSubject('');
      setEmailDoc(createEmptyEmailDocument());
    }
    setComposerTab('design');
    setView('compose');
  }

  function handleSave() {
    if (!subject.trim()) {
      toast.error('Subject line is required');
      return;
    }
    const childrenIds = (emailDoc.root?.data as any)?.childrenIds;
    if (!childrenIds || childrenIds.length === 0) {
      toast.error('Add at least one content block');
      return;
    }
    const payload = { subject, htmlContent: renderedHtml, jsonContent: emailDoc };
    if (editingCampaign) {
      updateMutation.mutate({ id: editingCampaign.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function openSendDialog(campaignId: number) {
    setSendingCampaignId(campaignId);
    setAudienceType('all');
    setSelectedTags([]);
    setSelectedEmails([]);
    setSubscriberSearch('');
    setShowSendConfirm(true);
  }

  function closeSendDialog() {
    setShowSendConfirm(false);
    setSendingCampaignId(null);
  }

  function openDeleteDialog(campaignId: number) {
    setDeletingCampaignId(campaignId);
    setShowDeleteConfirm(true);
  }

  function loadTemplate(template: Template) {
    if (template.json_content) {
      try {
        setEmailDoc(JSON.parse(template.json_content));
        toast.success(`Loaded template "${template.name}"`);
        setComposerTab('design');
      } catch {
        toast.error('Failed to parse template');
      }
    }
  }

  function toggleEmail(email: string) {
    setSelectedEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  }

  function toggleTag(tag: string) {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }

  // ── Stats ──

  const totalCampaigns = campaigns.length;
  const drafts = campaigns.filter((c) => c.status === 'draft').length;
  const sent = campaigns.filter((c) => c.status === 'sent').length;
  const totalDelivered = campaigns.reduce((sum, c) => sum + (c.delivered ?? 0), 0);
  const totalOpened = campaigns.reduce((sum, c) => sum + (c.opened ?? 0), 0);
  const avgOpenRate = totalDelivered > 0 ? ((totalOpened / totalDelivered) * 100).toFixed(1) : '0';

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'sending':
        return <Badge className="bg-yellow-100 text-yellow-800">Sending</Badge>;
      case 'sent':
        return <Badge variant="default">Sent</Badge>;
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-800">Scheduled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // ═══════════════════════════════════════════════════════
  // COMPOSE VIEW
  // ═══════════════════════════════════════════════════════
  if (view === 'compose') {
    const isSent = editingCampaign?.status === 'sent';

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={resetComposer}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {editingCampaign ? (isSent ? 'View Campaign' : 'Edit Campaign') : 'New Campaign'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {isSent
                  ? 'This campaign has already been sent'
                  : 'Design your newsletter and preview before sending'}
              </p>
            </div>
          </div>
          {!isSent && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                <FileText className="mr-2 h-4 w-4" />
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Draft'}
              </Button>
              {editingCampaign && editingCampaign.status === 'draft' && (
                <Button onClick={() => openSendDialog(editingCampaign.id)}>
                  <Send className="mr-2 h-4 w-4" />
                  Send Now
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Subject */}
        <Card>
          <CardContent className="pt-6">
            <Input
              placeholder="Newsletter subject line..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="text-lg font-medium"
              disabled={isSent}
            />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={composerTab} onValueChange={setComposerTab}>
          <TabsList>
            <TabsTrigger value="design" className="gap-1.5">
              <Palette className="h-4 w-4" />
              Design
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-1.5">
              <LayoutTemplate className="h-4 w-4" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-1.5">
              <Eye className="h-4 w-4" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="deliverability" className="gap-1.5">
              <ShieldCheck className="h-4 w-4" />
              Deliverability
            </TabsTrigger>
          </TabsList>

          {/* ── Design Tab ── */}
          <TabsContent value="design">
            <EmailBlockEditor value={emailDoc} onChange={setEmailDoc} />
          </TabsContent>

          {/* ── Templates Tab ── */}
          <TabsContent value="templates">
            <div className="space-y-4">
              {/* Save current design as template */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    Save Current Design as Template
                  </CardTitle>
                  <CardDescription>Reuse this design for future campaigns</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const childrenIds = (emailDoc.root?.data as any)?.childrenIds;
                      if (!childrenIds || childrenIds.length === 0) {
                        toast.error('Add content blocks in the Design tab first');
                        return;
                      }
                      setShowSaveTemplate(true);
                    }}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Save as Template
                  </Button>
                </CardContent>
              </Card>

              {/* Template gallery */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Your Templates</CardTitle>
                  <CardDescription>
                    Click a template to apply its design to this campaign
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {templates.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <LayoutTemplate className="text-muted-foreground h-10 w-10" />
                      <p className="text-muted-foreground mt-3 text-sm">
                        No templates yet. Design a newsletter and save it as a template.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {templates.map((tpl) => (
                        <Card
                          key={tpl.id}
                          className="hover:ring-primary/30 cursor-pointer transition-all hover:shadow-md hover:ring-2"
                          onClick={() => loadTemplate(tpl)}
                        >
                          {/* Preview thumbnail */}
                          <div className="h-32 overflow-hidden rounded-t-lg border-b bg-gray-50">
                            {tpl.html_preview ? (
                              <iframe
                                srcDoc={tpl.html_preview}
                                title={tpl.name}
                                className="pointer-events-none h-full w-full origin-top-left scale-50"
                                style={{ width: '200%', height: '200%' }}
                                sandbox=""
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center">
                                <LayoutTemplate className="text-muted-foreground h-8 w-8" />
                              </div>
                            )}
                          </div>
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-sm font-medium">{tpl.name}</p>
                                {tpl.description && (
                                  <p className="text-muted-foreground mt-0.5 text-xs">
                                    {tpl.description}
                                  </p>
                                )}
                                <p className="text-muted-foreground mt-1 text-xs">
                                  {format(new Date(tpl.updated_at), 'MMM d, yyyy')}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteTemplateMutation.mutate(tpl.id);
                                }}
                                title="Delete template"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── Preview Tab ── */}
          <TabsContent value="preview">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">Email Preview</CardTitle>
                    <CardDescription>How your subscribers will see this email</CardDescription>
                  </div>
                  <div className="flex rounded-md border">
                    <button
                      type="button"
                      className={`flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-xs font-medium ${
                        previewDevice === 'desktop'
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => setPreviewDevice('desktop')}
                    >
                      <Monitor className="h-3.5 w-3.5" />
                      Desktop
                    </button>
                    <button
                      type="button"
                      className={`flex items-center gap-1.5 rounded-r-md px-3 py-1.5 text-xs font-medium ${
                        previewDevice === 'mobile'
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => setPreviewDevice('mobile')}
                    >
                      <Smartphone className="h-3.5 w-3.5" />
                      Mobile
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div
                  className={`mx-auto min-h-[500px] rounded-md border bg-white transition-all ${
                    previewDevice === 'mobile' ? 'max-w-[375px]' : 'w-full'
                  }`}
                >
                  {renderedHtml ? (
                    <iframe
                      srcDoc={renderedHtml}
                      title="Newsletter preview"
                      className="w-full border-0"
                      style={{ height: previewDevice === 'mobile' ? '667px' : '600px' }}
                      sandbox=""
                    />
                  ) : (
                    <div className="text-muted-foreground flex h-[500px] items-center justify-center">
                      <p>Add content blocks in the Design tab to see a preview</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Deliverability Tab ── */}
          <TabsContent value="deliverability">
            <DeliverabilityPanel html={renderedHtml} subject={subject} />
          </TabsContent>
        </Tabs>

        {/* ── Send Confirmation Dialog ── */}
        <Dialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Send Newsletter</DialogTitle>
              <DialogDescription>Choose your audience and confirm sending.</DialogDescription>
            </DialogHeader>

            {/* Audience selector */}
            <div className="space-y-4">
              <div>
                <Label className="mb-2 text-sm font-medium">Send to</Label>
                <Select
                  value={audienceType}
                  onValueChange={(v) => setAudienceType(v as AudienceType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" /> All confirmed subscribers
                      </span>
                    </SelectItem>
                    <SelectItem value="tags">
                      <span className="flex items-center gap-2">
                        <Tag className="h-4 w-4" /> Filter by tags
                      </span>
                    </SelectItem>
                    <SelectItem value="selected">
                      <span className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4" /> Select specific subscribers
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Tag filter */}
              {audienceType === 'tags' && (
                <div className="space-y-2">
                  <Label className="text-xs">
                    Select tags (subscribers with any of these tags)
                  </Label>
                  {availableTags.length === 0 ? (
                    <p className="text-muted-foreground text-xs">No tags found on subscribers</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {availableTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTag(tag)}
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                            selectedTags.includes(tag)
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'hover:bg-muted'
                          }`}
                        >
                          {selectedTags.includes(tag) && <Check className="mr-1 h-3 w-3" />}
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedTags.length > 0 && (
                    <p className="text-muted-foreground text-xs">
                      {selectedTags.length} tag(s) selected
                    </p>
                  )}
                </div>
              )}

              {/* Subscriber selector */}
              {audienceType === 'selected' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="text-muted-foreground absolute left-2 top-2.5 h-4 w-4" />
                    <Input
                      placeholder="Search subscribers..."
                      value={subscriberSearch}
                      onChange={(e) => setSubscriberSearch(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  {selectedEmails.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedEmails.map((email) => (
                        <Badge key={email} variant="secondary" className="gap-1">
                          {email}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => toggleEmail(email)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="max-h-48 overflow-y-auto rounded-md border">
                    {availableSubscribers.length === 0 ? (
                      <p className="text-muted-foreground p-3 text-center text-xs">
                        No subscribers found
                      </p>
                    ) : (
                      availableSubscribers.map((sub) => (
                        <button
                          key={sub.email}
                          type="button"
                          onClick={() => toggleEmail(sub.email)}
                          className={`hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                            selectedEmails.includes(sub.email) ? 'bg-primary/5' : ''
                          }`}
                        >
                          <div
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              selectedEmails.includes(sub.email)
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-gray-300'
                            }`}
                          >
                            {selectedEmails.includes(sub.email) && <Check className="h-3 w-3" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm">{sub.email}</p>
                            {(sub.first_name || sub.last_name) && (
                              <p className="text-muted-foreground truncate text-xs">
                                {[sub.first_name, sub.last_name].filter(Boolean).join(' ')}
                              </p>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {selectedEmails.length} subscriber(s) selected
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeSendDialog}>
                Cancel
              </Button>
              <Button
                onClick={() => sendingCampaignId && sendMutation.mutate(sendingCampaignId)}
                disabled={
                  sendMutation.isPending ||
                  (audienceType === 'tags' && selectedTags.length === 0) ||
                  (audienceType === 'selected' && selectedEmails.length === 0)
                }
              >
                <Send className="mr-2 h-4 w-4" />
                {sendMutation.isPending
                  ? 'Sending...'
                  : audienceType === 'all'
                    ? 'Send to All'
                    : audienceType === 'tags'
                      ? `Send to Tagged`
                      : `Send to ${selectedEmails.length} Selected`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Save Template Dialog ── */}
        <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save as Template</DialogTitle>
              <DialogDescription>
                Save the current email design for reuse in future campaigns.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="mb-1 text-sm">Template Name</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. Weekly Newsletter, Product Update"
                />
              </div>
              <div>
                <Label className="mb-1 text-sm">Description (optional)</Label>
                <Input
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Brief description..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>
                Cancel
              </Button>
              <Button
                disabled={!templateName.trim() || saveTemplateMutation.isPending}
                onClick={() =>
                  saveTemplateMutation.mutate({
                    name: templateName,
                    description: templateDescription,
                    jsonContent: emailDoc,
                    htmlPreview: renderedHtml,
                  })
                }
              >
                <Save className="mr-2 h-4 w-4" />
                {saveTemplateMutation.isPending ? 'Saving...' : 'Save Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Newsletter Campaigns</h1>
          <p className="text-muted-foreground">Compose and send newsletters to your subscribers</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/projects/${projectId}/newsletter`}>
            <Button variant="outline">
              <Users className="mr-2 h-4 w-4" />
              Subscribers
            </Button>
          </Link>
          <Button onClick={() => openComposer()}>
            <Plus className="mr-2 h-4 w-4" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Campaigns</CardTitle>
            <Mail className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCampaigns}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Drafts</CardTitle>
            <FileText className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{drafts}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sent</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sent}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Open Rate</CardTitle>
            <BarChart3 className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgOpenRate}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns Table */}
      <Card>
        <CardHeader>
          <CardTitle>Campaigns</CardTitle>
          <CardDescription>All your newsletter campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Mail className="text-muted-foreground h-12 w-12" />
              <h3 className="mt-4 text-lg font-semibold">No campaigns yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first newsletter campaign to engage your subscribers
              </p>
              <Button onClick={() => openComposer()}>
                <Plus className="mr-2 h-4 w-4" />
                Create Campaign
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="text-right">Recipients</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Opened</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell className="max-w-[300px] truncate font-medium">
                      {campaign.subject}
                    </TableCell>
                    <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                    <TableCell>
                      {campaign.sent_at ? format(new Date(campaign.sent_at), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell className="text-right">{campaign.recipients ?? '-'}</TableCell>
                    <TableCell className="text-right">{campaign.delivered ?? '-'}</TableCell>
                    <TableCell className="text-right">{campaign.opened ?? '-'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {campaign.status === 'draft' && (
                            <>
                              <DropdownMenuItem onClick={() => openComposer(campaign)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openSendDialog(campaign.id)}>
                                <Send className="mr-2 h-4 w-4" />
                                Send Now
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => openDeleteDialog(campaign.id)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                          {campaign.status === 'sent' && (
                            <DropdownMenuItem onClick={() => openComposer(campaign)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Campaign</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this draft campaign? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingCampaignId && deleteMutation.mutate(deletingCampaignId)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Send Confirmation Dialog (from list view) ── */}
      <Dialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Newsletter</DialogTitle>
            <DialogDescription>Choose your audience and confirm sending.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="mb-2 text-sm font-medium">Send to</Label>
              <Select
                value={audienceType}
                onValueChange={(v) => setAudienceType(v as AudienceType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All confirmed subscribers</SelectItem>
                  <SelectItem value="tags">Filter by tags</SelectItem>
                  <SelectItem value="selected">Select specific subscribers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {audienceType === 'tags' && (
              <div className="space-y-2">
                <Label className="text-xs">Select tags</Label>
                <div className="flex flex-wrap gap-1.5">
                  {availableTags.length === 0 ? (
                    <p className="text-muted-foreground text-xs">No tags found</p>
                  ) : (
                    availableTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                          selectedTags.includes(tag)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        {selectedTags.includes(tag) && <Check className="mr-1 h-3 w-3" />}
                        {tag}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {audienceType === 'selected' && (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="text-muted-foreground absolute left-2 top-2.5 h-4 w-4" />
                  <Input
                    placeholder="Search subscribers..."
                    value={subscriberSearch}
                    onChange={(e) => setSubscriberSearch(e.target.value)}
                    className="pl-8"
                  />
                </div>
                {selectedEmails.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedEmails.map((email) => (
                      <Badge key={email} variant="secondary" className="gap-1">
                        {email}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => toggleEmail(email)} />
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="max-h-48 overflow-y-auto rounded-md border">
                  {availableSubscribers.map((sub) => (
                    <button
                      key={sub.email}
                      type="button"
                      onClick={() => toggleEmail(sub.email)}
                      className={`hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                        selectedEmails.includes(sub.email) ? 'bg-primary/5' : ''
                      }`}
                    >
                      <div
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          selectedEmails.includes(sub.email)
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedEmails.includes(sub.email) && <Check className="h-3 w-3" />}
                      </div>
                      <span className="truncate">{sub.email}</span>
                    </button>
                  ))}
                </div>
                <p className="text-muted-foreground text-xs">{selectedEmails.length} selected</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeSendDialog}>
              Cancel
            </Button>
            <Button
              onClick={() => sendingCampaignId && sendMutation.mutate(sendingCampaignId)}
              disabled={
                sendMutation.isPending ||
                (audienceType === 'tags' && selectedTags.length === 0) ||
                (audienceType === 'selected' && selectedEmails.length === 0)
              }
            >
              <Send className="mr-2 h-4 w-4" />
              {sendMutation.isPending ? 'Sending...' : 'Confirm Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
